import copy from "copy-to-clipboard";
import { FunctionComponent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AddonOptions from "rivals/components/shared/AddonOptions";
import AddonModal from "rivals/components/shared/AddonOptions/AddonModal";
import {
  ADDON_MODAL_CONSTANTS,
  PURCHASE_ADDON_MODAL_STEP,
  UPSELL_FUNNEL_ADDON_MODAL_STEP
} from "rivals/components/shared/AddonOptions/constants";
import Button from "rivals/components/shared/Button";
import Link from "rivals/components/shared/Link";
import Modal from "rivals/components/shared/Modal";
import Spinner from "rivals/components/shared/Spinner";
import SiteSelect from "rivals/components/shared/Subscription/SiteSelect";
import SubscriptionCard from "rivals/components/shared/Subscription/SubscriptionCard";
import {
  getUserEligibleAddons as getUserEligibleAddonsAction,
  removeAddOn,
  resetErrors,
  setActiveAddOn,
  setModalStep
} from "rivals/redux/subscriptionAddons/actions";
import {
  getActiveAddOn,
  getAddOnPurchaseSuccess,
  getFilteredNonPurchasedChooseAddOns,
  getRemoveAddOnSuccess,
  getUserEligibleAddons,
  getSubmitting as subscriptionAddOnGetSubmitting
} from "rivals/redux/subscriptionAddons/selectors";
import { getSubscriptions } from "rivals/redux/user/actions";
import { HOST_BASE_PATH, PROTOCOL } from "rivals/services/base";
import {
  CONTACT_US,
  OBI_ADD_ON_ERROR,
  PATHS,
  REACTIVATE
} from "rivals/shared/constants";
import { AddOn } from "rivals/shared/interfaces/AddOn";
import { Site } from "rivals/shared/interfaces/Site";
import {
  getAssociatedSubscriptionId,
  isAddOnSubscribed
} from "rivals/shared/utils/addons";
import { setData } from "rivals/shared/utils/qa";
import styles from "./Subscriptions.module.scss";
import strings from "./strings";
import { DispatchProps, StateProps, SubscriptionDetails } from "./types";

const { ALL_ACCESS, BUNDLE, SPY } = ADDON_MODAL_CONSTANTS;
export type Props = DispatchProps & StateProps;

const UserSubscriptions: FunctionComponent<Props> = ({
  isLoading,
  isSubmitting,
  reactivateSubscription,
  sendGAEvent,
  subscriptions,
  subscriptionsConstants
}) => {
  if (!subscriptionsConstants) {
    return null;
  }
  const dispatch = useDispatch();
  const subscriptionAddOnSubmitting = useSelector(
    subscriptionAddOnGetSubmitting
  );
  const addOns = useSelector(getUserEligibleAddons);
  const filteredChooseAddOns = useSelector(getFilteredNonPurchasedChooseAddOns);
  const removeAddOnSuccess = useSelector(getRemoveAddOnSuccess);
  const purchaseAddOnSuccess = useSelector(getAddOnPurchaseSuccess);

  const activeAddOn = useSelector(getActiveAddOn);

  const hasAllAccess =
    addOns && addOns.length && addOns.some(addOn => addOn.type === ALL_ACCESS);
  const bundleAddOn =
    addOns && addOns.length && addOns.find(addOn => addOn.type === BUNDLE);

  const [showModal, toggleShowModal] = useState(false);
  const [showAddOnRemoveModal, toggleShowAddOnRemoveModal] = useState(true);
  const [showAddOnModal, toggleShowAddOnModal] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState<number | null>(
    null
  );

  const [selectedSubscription, selectSubscription] = useState<
    SubscriptionDetails
  >({});

  const [selectedAddOn, selectAddOn] = useState<AddOn | null>(null);

  const removeAddOnFailure = removeAddOnSuccess === false;

  useEffect(() => {
    if (!isSubmitting) {
      toggleShowModal(false);
      selectSubscription({});
    }
  }, [isSubmitting]);

  useEffect(() => {
    if (
      !subscriptionAddOnSubmitting &&
      (removeAddOnSuccess || purchaseAddOnSuccess)
    ) {
      dispatch(getSubscriptions());
      dispatch(getUserEligibleAddonsAction());
    }
  }, [purchaseAddOnSuccess, removeAddOnSuccess]);

  const {
    addSubscriptionButtonText,
    addSubscriptionTitle,
    cancelSubscriptionText,
    copyLinkButtonText,
    header,
    referralDescription,
    referralDisclaimer,
    referralTitle,
    referralUrl,
    referralUrlTitle,
    selectATeam,
    upgradeToAnnual,
    userInitiatedCancellationsEnabled
  } = subscriptionsConstants;

  const [subscriptionSelection, setSubscriptionSelection] = useState<
    Site | undefined
  >();

  const handleSelectedSiteCleared = (): void => {
    setSubscriptionSelection(undefined);
  };

  const [copyUrlButtonText, setCopyUrlButtonText] = useState<string>(
    copyLinkButtonText
  );

  const copyUrlToClipboard = (): void => {
    copy(referralUrl)
      ? setCopyUrlButtonText("Copied")
      : setCopyUrlButtonText(copyLinkButtonText);
  };

  function showReactivateModal(subscriptionDetails: SubscriptionDetails): void {
    toggleShowModal(true);
    selectSubscription(subscriptionDetails);
  }

  function showRemoveAddOnModal(addOn: AddOn | null): void {
    toggleShowAddOnRemoveModal(!!addOn);
    selectAddOn(addOn);
  }

  function buyAddOnModalTrigger(addOn: AddOn): void {
    if (addOn.type === SPY) {
      if (
        hasAllAccess ||
        (bundleAddOn &&
          bundleAddOn.associatedSubscription &&
          !bundleAddOn.associatedSubscription.subscribed)
      ) {
        dispatch(setModalStep(UPSELL_FUNNEL_ADDON_MODAL_STEP));
      } else {
        dispatch(setModalStep(PURCHASE_ADDON_MODAL_STEP));
      }
    } else {
      dispatch(setModalStep(PURCHASE_ADDON_MODAL_STEP));
    }
    dispatch(setActiveAddOn(addOn));
    toggleShowAddOnModal(true);
  }

  function filteredAddOns(subscriptionId: number): AddOn[] {
    if (!addOns || !addOns.length) {
      return [];
    } else {
      return addOns.filter(
        addOn =>
          getAssociatedSubscriptionId(addOn) === subscriptionId &&
          !isAddOnSubscribed(addOn)
      );
    }
  }

  let subscriptionCards: JSX.Element | JSX.Element[];

  if (isLoading) {
    subscriptionCards = <Spinner />;
  } else if (subscriptions && subscriptions.length) {
    subscriptionCards = subscriptions.map(sub => (
      <SubscriptionCard
        buyAddOnModalTrigger={buyAddOnModalTrigger}
        cancelSubscriptionText={cancelSubscriptionText}
        editingSubscription={editingSubscription}
        key={sub.id}
        sendGAEvent={sendGAEvent}
        setEditingSubscription={setEditingSubscription}
        showReactivateModal={(): void => {
          showReactivateModal({
            id: sub.id,
            name: sub.site.name,
            plan: sub.description
          });
        }}
        showRemoveAddOnModal={showRemoveAddOnModal}
        subscription={sub}
        upgradeText={upgradeToAnnual}
        upsellAddOns={filteredAddOns(sub.id)}
        userInitiatedCancellationsEnabled={userInitiatedCancellationsEnabled}
        {...setData("subscriptions-page", "sub-box")}
      />
    ));
  } else {
    subscriptionCards = <SubscriptionCard />;
  }

  const cancelLinkDataPhilter = "subscriptions-page-contact-us";

  const cancelLink = userInitiatedCancellationsEnabled ? (
    <div className={styles.contactUs}>
      {`${strings.customerSupportDescriptor} `}
      <Link data={cancelLinkDataPhilter} href={PATHS.HELP}>
        {CONTACT_US}
      </Link>
    </div>
  ) : (
    <Link
      className={styles.cancelSubscriptionLink}
      data={cancelLinkDataPhilter}
      href={PATHS.HELP}
    >
      {cancelSubscriptionText}
    </Link>
  );

  const subscriptionsSelect = (
    <SiteSelect
      onChange={(value: Site): void => {
        setSubscriptionSelection(value);
      }}
      onClear={handleSelectedSiteCleared}
      placeholder={selectATeam}
    />
  );

  const addSubscription = (
    <>
      <div
        className={styles.addSubscriptionTitle}
        {...setData("subscriptions-page", "add-subscription-header")}
      >
        {addSubscriptionTitle}
      </div>
      {subscriptionsSelect}
      <Button
        buttonText={addSubscriptionButtonText}
        className={styles.addSubscriptionButton}
        data="subscriptions-page-add-subscription"
        disabled={!subscriptionSelection}
        inverted
        size="small"
        url={
          subscriptionSelection &&
          `${PROTOCOL}${subscriptionSelection.subdomain}.${HOST_BASE_PATH}${PATHS.NEW_SUBSCRIPTION}`
        }
      />
    </>
  );

  const promoSection =
    referralUrl && referralUrl.length ? (
      <>
        <hr className={styles.divider} />
        <div className={styles.referralTitle}>{referralTitle}</div>
        <div className={styles.referralDescription}>{referralDescription}</div>
        <div className={styles.referralUrlTitle}>{referralUrlTitle}</div>
        <div className={styles.referralUrl}>{referralUrl}</div>
        <Button
          buttonText={copyUrlButtonText}
          className={styles.copyLinkButton}
          inverted
          onClick={copyUrlToClipboard}
          size="small"
        />
        <div className={styles.referralDisclaimer}>{referralDisclaimer}</div>
        <hr className={styles.divider} />
      </>
    ) : null;

  const removeAddOnModal = selectedAddOn &&
    selectedAddOn.associatedSubscription && (
      <Modal
        cancelButtonText={removeAddOnFailure ? "Close" : "Don't Remove"}
        confirmButtonText={"Remove"}
        disabled={subscriptionAddOnSubmitting || removeAddOnFailure}
        handleCancel={(): void => {
          toggleShowAddOnRemoveModal(false);
          dispatch(setActiveAddOn());
          dispatch(resetErrors());
        }}
        handleConfirm={(): void => {
          if (selectedAddOn.associatedSubscription) {
            dispatch(
              removeAddOn(
                selectedAddOn.associatedSubscription.id,
                selectedAddOn.id
              )
            );
          }
        }}
        open={showAddOnRemoveModal}
        title={"Remove Add-On"}
      >
        <div className={styles.reactivateModalBody}>
          {strings.cancelAddOnText(selectedAddOn.name)}
        </div>
        {subscriptionAddOnSubmitting && <Spinner />}
        <div>
          {removeAddOnFailure && (
            <div className={styles.errorText}>{OBI_ADD_ON_ERROR}</div>
          )}
        </div>
      </Modal>
    );

  return (
    <>
      <div className={styles.container}>
        <h1
          className={styles.header}
          {...setData("subscriptions-page", "header")}
        >
          {!editingSubscription && header}
        </h1>
        {subscriptionCards}
        {cancelLink}
        <div className={styles.addOnsListContainer}>
          <AddonOptions />
        </div>
        {promoSection}
        {addSubscription}
      </div>
      <Modal
        confirmButtonText={REACTIVATE}
        disabled={isSubmitting}
        handleCancel={(): void => {
          toggleShowModal(false);
          selectSubscription({});
        }}
        handleConfirm={(): void => {
          if (selectedSubscription.id) {
            reactivateSubscription(selectedSubscription.id);
          }
        }}
        open={showModal}
        title={REACTIVATE}
      >
        <div className={styles.reactivateModalBody}>
          <span>{selectedSubscription.name}</span>
          <span>{selectedSubscription.plan}</span>
        </div>
      </Modal>

      {removeAddOnModal}

      {!!activeAddOn && (
        <AddonModal
          addOn={activeAddOn}
          addOns={addOns && addOns.concat(filteredChooseAddOns)}
          closeModal={(): void => {
            toggleShowAddOnModal(false);
          }}
          showModal={showAddOnModal}
        />
      )}
    </>
  );
};

export default UserSubscriptions;
